import { graphqlClient } from '@/lib/graphql-client';
import {
  dateOnlyOf,
  dateOnlyRangeClause,
  weekdayNameOf,
} from '@/lib/utils/custom-date/custom-date';
import { attendanceDateClause } from '@/modules/time-attendance/attendance/services/attendance-rules';
import type { AttendanceRecord } from '@/modules/time-attendance/attendance/types/attendance.types';
import { buildGetAttendancesQuery } from '@/modules/time-attendance/team-attendance/graphql/queries';
import { buildGetLeaveRequestsForCalendarQuery } from '@/modules/time-off/calendar/graphql/queries';
import { buildGetHrProfilesQuery } from '../graphql/queries';
import { buildGetHolidaysForPresenceQuery } from '../graphql/presence-queries';
import { currentWorkDaysOf, HrProfileRow } from './employee-mappers';

/** What a person's presence today is worked out from (see `derivePresence`). */
export interface PresenceFacts {
  record: AttendanceRecord | null;
  leaveToday: { lastDay: string } | null;
  isWorkDay: boolean;
  isHoliday: boolean;
  holidayName?: string;
}

interface LeaveRow {
  UserId: string;
  StartDate: string;
  EndDate: string;
  Status: string;
}

interface HolidayRow {
  Name: string;
  StartDate: string;
  IsRegional?: boolean | null;
  CantonIds?: string[] | null;
}

/** HR terms read per person; generous, since a page of people is read in one query. */
const TERMS_PER_PERSON = 20;

/**
 * Today's facts for each of `userIds` on the organization day `day` ("YYYY-MM-DD"), read in four queries
 * for the whole page: the day's attendance records, approved leave covering the day, the day's holidays and
 * the people's HR terms (for their work days). The data rules decide which rows come back; a person whose
 * records the viewer can't read just looks like one with none.
 *
 * A holiday counts for everyone unless it is regional: regional holidays need the person's canton, which
 * the list doesn't carry, so they are left out here.
 */
export const getEmployeesPresenceFacts = async (
  userIds: string[],
  day: string
): Promise<Record<string, PresenceFacts>> => {
  if (userIds.length === 0) return {};
  const today = dateOnlyRangeClause(day, day);
  const [attendances, leaves, holidays, terms] = await Promise.all([
    graphqlClient.query<{ Attendances: { items: AttendanceRecord[] } }>({
      query: buildGetAttendancesQuery(
        { UserId: { $in: userIds }, AttendanceDate: attendanceDateClause(day) },
        1,
        userIds.length * 2
      ),
    }),
    graphqlClient.query<{ LeaveRequests: { items: LeaveRow[] } }>({
      query: buildGetLeaveRequestsForCalendarQuery(
        {
          UserId: { $in: userIds },
          Status: 'Approved',
          // Both days are date only and inclusive: the leave starts by today and ends today or later.
          ...(today ? { StartDate: { $lt: today.$lt }, EndDate: { $gte: today.$gte } } : {}),
        },
        1,
        userIds.length * 2
      ),
    }),
    graphqlClient.query<{ Holidays: { items: HolidayRow[] } }>({
      query: buildGetHolidaysForPresenceQuery({ StartDate: today }, 50),
    }),
    graphqlClient.query<{ HrProfiles: { items: HrProfileRow[] } }>({
      query: buildGetHrProfilesQuery(
        { UserId: { $in: userIds } },
        1,
        userIds.length * TERMS_PER_PERSON
      ),
    }),
  ]);

  const holiday = (holidays.Holidays.items ?? []).find(
    (item) => !item.IsRegional || !item.CantonIds?.length
  );
  const weekday = weekdayNameOf(day);

  return Object.fromEntries(
    userIds.map((userId) => {
      const record =
        (attendances.Attendances.items ?? []).find((item) => item.UserId === userId) ?? null;
      const leave = (leaves.LeaveRequests.items ?? [])
        .filter((item) => item.UserId === userId)
        .sort((a, b) => dateOnlyOf(b.EndDate).localeCompare(dateOnlyOf(a.EndDate)))[0];
      const ownTerms = (terms.HrProfiles.items ?? [])
        .filter((term) => term.UserId === userId)
        .sort((a, b) => (a.HRPeriod ?? 0) - (b.HRPeriod ?? 0));
      const workDays = currentWorkDaysOf(ownTerms);
      return [
        userId,
        {
          record,
          leaveToday: leave ? { lastDay: dateOnlyOf(leave.EndDate) } : null,
          isWorkDay: Boolean(weekday && workDays.includes(weekday)),
          isHoliday: Boolean(holiday),
          holidayName: holiday?.Name,
        } satisfies PresenceFacts,
      ];
    })
  );
};
